import { emit } from './bus.js'
import { parsePgn } from './pgn.js'
import { activeIndexAt } from './timeline.js'
import { distributeStamps } from './distribute.js'
import { DEFAULT_OVERLAY, DEFAULT_PREFS } from '../config.js'

// Central app state. UI modules read this object and subscribe to bus events;
// all mutations go through the exported mutators so events + autosave fire.

export const state = {
  pgn: { raw: '', headers: {}, moves: [], startFen: null, error: null },
  stamps: [], // seconds | null, parallel to pgn.moves
  selection: -1,
  video: { url: null, fileName: null, size: 0, duration: 0, width: 0, height: 0, fps: 30 },
  overlay: { ...DEFAULT_OVERLAY },
  prefs: { ...DEFAULT_PREFS },
  // True while every stamp is an auto-generated guide (pre-fill/distribute).
  // Cleared the moment the user tags anything manually — drives the one-tap
  // "clear guide times" banner.
  scaffolded: false,
  // Transient playback state (not persisted). activeIndex = index of the move
  // whose position is currently shown, -1 = start position.
  playback: { time: 0, playing: false, activeIndex: -1 },
}

// Persistence is wired up by storage.js at boot; a no-op until then.
let persist = () => {}
export function setPersistence(fn) {
  persist = fn
}

export function loadPgnText(raw) {
  const parsed = parsePgn(raw)
  state.pgn = { raw, ...parsed }
  state.stamps = parsed.moves.map(() => null)
  state.selection = parsed.moves.length ? 0 : -1
  emit('pgn')
  emit('stamps')
  emit('selection', { reveal: true })
  persist()
  maybeAutoFill()
  return parsed
}

export function setPrefs(patch) {
  Object.assign(state.prefs, patch)
  emit('prefs')
  persist()
}

// Pre-fills a blank timestamp sheet as an editable scaffold. Only fires when
// the pref is on, a game and a usable duration exist, and nothing is tagged.
function maybeAutoFill() {
  if (!state.prefs.autoFill || hasStamps()) return
  const stamps = distributeStamps({
    count: state.pgn.moves.length,
    duration: state.video.duration,
    curve: state.prefs.curve,
  })
  if (!stamps.length) return
  state.stamps = stamps
  state.scaffolded = true
  emit('stamps')
  emit('autofill')
  persist()
}

export function hasStamps() {
  return state.stamps.some((s) => s != null)
}

export function setStamp(index, seconds) {
  if (index < 0 || index >= state.stamps.length) return
  state.stamps[index] = Math.max(0, seconds)
  state.scaffolded = false
  emit('stamps')
  persist()
}

export function clearStamp(index) {
  if (index < 0 || index >= state.stamps.length) return
  state.stamps[index] = null
  emit('stamps')
  persist()
}

export function replaceStamps(stamps) {
  state.stamps = state.pgn.moves.map((_, i) => {
    const value = stamps?.[i]
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
  })
  state.scaffolded = false
  emit('stamps')
  persist()
}

// Like replaceStamps, but marks the result as an auto-generated guide.
export function applyScaffold(stamps) {
  replaceStamps(stamps)
  state.scaffolded = state.stamps.some((s) => s != null)
  emit('stamps')
  persist()
}

export function selectMove(index, { reveal = false } = {}) {
  const max = state.pgn.moves.length - 1
  state.selection = Math.max(-1, Math.min(max, index))
  emit('selection', { reveal })
}

export function setVideoFile({ url, fileName, size }) {
  if (state.video.url) URL.revokeObjectURL(state.video.url)
  state.video = { ...state.video, url, fileName, size, duration: 0, width: 0, height: 0 }
  emit('video')
  persist()
}

export function setVideoMeta({ duration, width, height }) {
  Object.assign(state.video, { duration, width, height })
  emit('video')
  persist()
  maybeAutoFill()
}

export function setFps(fps) {
  if (Number.isFinite(fps) && fps > 0) {
    state.video.fps = fps
    emit('video')
    persist()
  }
}

export function patchOverlay(patch) {
  Object.assign(state.overlay, patch)
  emit('overlay')
  persist()
}

// Restores a saved/imported project snapshot (see storage.buildSnapshot).
// The video file itself can't be persisted — its metadata is kept so exports
// still work and the player can prompt to re-attach the same file.
export function restoreProject(snapshot) {
  const raw = typeof snapshot.pgn === 'string' ? snapshot.pgn : ''
  const parsed = parsePgn(raw)
  state.pgn = { raw, ...parsed }
  state.stamps = parsed.moves.map((_, i) => {
    const value = snapshot.stamps?.[i]
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
  })
  const maxIndex = parsed.moves.length - 1
  state.selection = Math.max(-1, Math.min(maxIndex, snapshot.selection ?? (maxIndex >= 0 ? 0 : -1)))
  const v = snapshot.video ?? {}
  state.video = {
    ...state.video,
    url: null,
    fileName: v.fileName ?? null,
    size: v.size ?? 0,
    duration: Number.isFinite(v.duration) ? v.duration : 0,
    width: v.width ?? 0,
    height: v.height ?? 0,
    fps: Number.isFinite(v.fps) && v.fps > 0 ? v.fps : 30,
  }
  state.overlay = { ...DEFAULT_OVERLAY, ...(snapshot.overlay ?? {}) }
  state.prefs = { ...DEFAULT_PREFS, ...(snapshot.prefs ?? {}) }
  state.scaffolded = snapshot.scaffolded === true
  emit('pgn')
  emit('stamps')
  emit('video')
  emit('overlay')
  emit('prefs')
  emit('selection', { reveal: true })
  persist()
}

export function setPlaybackTime(time, playing) {
  state.playback.time = time
  state.playback.playing = playing
  state.playback.activeIndex = activeIndexAt(state.stamps, time)
  emit('time')
}
